import { toast, useIsPlatformHost } from '@bemor/web-kit';
import {
    Button,
    OpenExternalIcon,
    RowActions,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { Tenant } from '@/api/types';
import {
    handoff,
    HANDOFF_LABEL,
    hasHost,
    NO_HOST,
    type HandoffKind,
} from '@/lib/handoff';

const KINDS: HandoffKind[] = ['admin', 'ledger'];

/** BM-94: the tenant's documentation host (its `docs` domain), a plain link; null when it has none. */
export function docsUrl(tenant: Pick<Tenant, 'domains'>): string | null {
    const domain =
        tenant.domains.find((d) => d.kind === 'docs' && d.is_default) ??
        tenant.domains.find((d) => d.kind === 'docs');
    return domain ? `https://${domain.hostname}` : null;
}

/**
 * BM-68: "Open admin" and "Open Ledger" on a tenant, platform host only (the hand-off is refused anywhere else).
 * A kind the tenant has no host for is disabled and says so. The browser leaves for the tenant host.
 * BM-94: "Open docs" beside them, a plain link to the tenant's docs host when it has one.
 */
export function OpenTenant({
    tenant,
    menu = false,
    name,
}: {
    tenant: Pick<Tenant, 'id' | 'domains'>;
    /** BM-84 (rule G.6, design 0.7.0 tenants): in a list row, the two hand-offs are the row's "…" menu. */
    menu?: boolean;
    /** The row's name for the menu's accessible label. */
    name?: string;
}) {
    const platformHost = useIsPlatformHost();
    const [busy, setBusy] = useState<HandoffKind | null>(null);
    const [error, setError] = useState<string | null>(null);
    if (!platformHost) return null;
    const open = async (kind: HandoffKind) => {
        setBusy(kind);
        setError(null);
        const url = await handoff(tenant.id, kind).catch(() => null);
        if (url) {
            window.location.assign(url);
            return;
        }
        setBusy(null);
        setError('The board could not be opened. Try again.');
        if (menu) toast.error('The board could not be opened. Try again.');
    };
    const docs = docsUrl(tenant);
    if (menu)
        return (
            <RowActions
                name={name}
                data-testid="open-tenant"
                items={[
                    ...KINDS.map((kind) => {
                        const ok = hasHost(tenant, kind);
                        return {
                            key: kind,
                            label: ok ? HANDOFF_LABEL[kind] : NO_HOST[kind],
                            icon: OpenExternalIcon,
                            disabled: !ok || busy !== null,
                            onSelect: () => void open(kind),
                        };
                    }),
                    {
                        key: 'docs',
                        label: docs ? 'Open docs' : 'No docs host yet',
                        icon: OpenExternalIcon,
                        disabled: !docs,
                        onSelect: () => {
                            if (docs) window.open(docs, '_blank', 'noopener');
                        },
                    },
                ]}
            />
        );
    return (
        <span
            className="inline-flex flex-wrap items-center gap-2"
            data-testid="open-tenant"
            onClick={(e) => e.stopPropagation()}
        >
            {KINDS.map((kind) => {
                const ok = hasHost(tenant, kind);
                const button = (
                    <Button
                        key={kind}
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!ok || busy !== null}
                        onClick={() => void open(kind)}
                    >
                        {HANDOFF_LABEL[kind]}
                    </Button>
                );
                if (ok) return button;
                // BM-84 (rule G.5): why it is disabled, as a Tooltip a keyboard user can reach
                return (
                    <Tooltip key={kind}>
                        <TooltipTrigger asChild>
                            <span tabIndex={0} className="inline-flex">
                                {button}
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>{NO_HOST[kind]}</TooltipContent>
                    </Tooltip>
                );
            })}
            {docs ? (
                <Button asChild size="sm" variant="outline">
                    <a href={docs} target="_blank" rel="noopener noreferrer">
                        Open docs
                    </a>
                </Button>
            ) : null}
            {error ? (
                <span role="alert" className="text-destructive text-xs">
                    {error}
                </span>
            ) : null}
        </span>
    );
}
